import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { WorkflowScenario } from "./atlas-workflow/WorkflowScenario";
import { ResponsibilityAnimation, RESPONSIBILITY_DURATIONS } from "./atlas-workflow/ResponsibilityAnimation";
import { PricingAnimation, PRICING_DURATIONS } from "./atlas-workflow/PricingAnimation";
import { AnalyticsAnimation, ANALYTICS_DURATIONS } from "./atlas-workflow/AnalyticsAnimation";

type Scenario = "responsibility" | "pricing" | "analytics";

const SCENARIOS: Scenario[] = ["responsibility", "pricing", "analytics"];

const STEP_KEYS: Record<Scenario, string[]> = {
  responsibility: ["step1", "step2", "step3", "step4", "step5", "step6", "step7", "step8"],
  pricing: ["step1", "step2", "step3", "step4", "step5", "step6"],
  analytics: ["step1", "step2", "step3", "step4", "step5"],
};

const DURATIONS: Record<Scenario, number[]> = {
  responsibility: RESPONSIBILITY_DURATIONS,
  pricing: PRICING_DURATIONS,
  analytics: ANALYTICS_DURATIONS,
};

export function AtlasWorkflowAnimation() {
  const { t } = useTranslation();
  const [active, setActive] = useState<Scenario>("responsibility");
  const [scenarioKey, setScenarioKey] = useState(0);

  const handleTabChange = useCallback((scenario: Scenario) => {
    setActive(scenario);
    setScenarioKey((k) => k + 1);
  }, []);

  const steps = STEP_KEYS[active].map((key) =>
    t(`productPages.atlas.workflow.${active}.${key}`)
  );

  const renderAnimation = useCallback(
    (currentStep: number, cycleKey: number) => {
      switch (active) {
        case "responsibility":
          return <ResponsibilityAnimation currentStep={currentStep} cycleKey={cycleKey} />;
        case "pricing":
          return <PricingAnimation currentStep={currentStep} cycleKey={cycleKey} />;
        case "analytics":
          return <AnalyticsAnimation currentStep={currentStep} cycleKey={cycleKey} />;
      }
    },
    [active]
  );

  return (
    <div>
      <h2 className="text-2xl lg:text-3xl font-heading font-semibold text-foreground text-center mb-2">
        {t("productPages.atlas.workflow.title")}
      </h2>
      <p className="text-muted-foreground text-center mb-6 lg:mb-8 font-body max-w-xl mx-auto">
        {t("productPages.atlas.workflow.subtitle")}
      </p>

      {/* Tabs */}
      <div className="flex justify-center gap-2 mb-8">
        {SCENARIOS.map((scenario) => (
          <button
            key={scenario}
            onClick={() => handleTabChange(scenario)}
            className="px-4 py-2 rounded-lg text-sm font-heading font-medium transition-colors duration-300"
            style={{
              backgroundColor: active === scenario ? "hsl(var(--petrol))" : "hsl(var(--muted))",
              color: active === scenario ? "white" : "hsl(var(--muted-foreground))",
            }}
          >
            {t(`productPages.atlas.workflow.tabs.${scenario}`)}
          </button>
        ))}
      </div>

      <WorkflowScenario
        key={`${active}-${scenarioKey}`}
        steps={steps}
        stepDurations={DURATIONS[active]}
        renderAnimation={renderAnimation}
      />
    </div>
  );
}
